import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Target, Users, CheckCircle, ArrowRight, BookOpen } from 'lucide-react';
import Button from '../../../components/ui/Button';

const InterviewPrepPublicPreview = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "MCQ Practice Bank",
      description: "1000+ testing questions covering Manual, Automation, API Testing, and Frameworks with detailed explanations.",
      preview: "Try 5 sample questions"
    },
    {
      icon: <Play className="w-8 h-8" />,
      title: "Mock Interview Sessions",
      description: "Realistic interview scenarios with AI-powered feedback and performance analysis.",
      preview: "Watch demo interview"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Company-Specific Prep",
      description: "Tailored preparation materials for top tech companies including FAANG interview patterns.",
      preview: "View sample questions"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Peer Practice Groups",
      description: "Connect with other testing professionals for collaborative interview preparation.",
      preview: "Join community"
    }
  ];

  const sampleQuestions = [
    {
      question: "What is the difference between Verification and Validation in testing?",
      difficulty: "Basic",
      category: "Manual Testing",
      options: [
        "Verification checks 'are we building the right product', Validation checks 'are we building the product right'",
        "Verification checks 'are we building the product right', Validation checks 'are we building the right product'",
        "Both are the same thing",
        "Verification is for unit testing, Validation is for integration testing"
      ],
      correct: 1
    },
    {
      question: "Which Selenium WebDriver method is used to handle dropdowns?",
      difficulty: "Intermediate", 
      category: "Automation Testing",
      options: [
        "selectDropdown()",
        "Select class",
        "dropdownSelect()",
        "chooseOption()"
      ],
      correct: 1
    }
  ];

  const companies = [
    { name: "Google", logo: "🔵", questions: 150 },
    { name: "Microsoft", logo: "🟦", questions: 135 },
    { name: "Amazon", logo: "🟧", questions: 180 },
    { name: "Meta", logo: "🔵", questions: 120 },
    { name: "Netflix", logo: "🔴", questions: 95 },
    { name: "Apple", logo: "⚫", questions: 110 }
  ];

  const benefits = [
    "Track your interview readiness score",
    "Get personalized weak area analysis", 
    "Access premium mock interview sessions",
    "Company-specific preparation materials",
    "Peer practice group matching",
    "AI-powered performance insights"
  ];

  return (
    <div className="space-y-12">
      {/* Hero Preview Section */}
      <div className="text-center space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Master Testing Interviews with 
            <span className="block bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
              Comprehensive Preparation
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join 2,500+ testing professionals who've successfully landed their dream jobs using our 
            comprehensive interview preparation platform covering all testing disciplines.
          </p>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="text-2xl font-bold text-blue-600">1000+</div>
            <div className="text-sm text-blue-800 font-medium">Practice Questions</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
            <div className="text-2xl font-bold text-purple-600">50+</div>
            <div className="text-sm text-purple-800 font-medium">Mock Scenarios</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-100">
            <div className="text-2xl font-bold text-green-600">94%</div>
            <div className="text-sm text-green-800 font-medium">Success Rate</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
            <div className="text-2xl font-bold text-orange-600">12</div>
            <div className="text-sm text-orange-800 font-medium">Top Companies</div>
          </div>
        </div>
      </div>
      {/* Features Preview */}
      <div>
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
          What You'll Get Access To
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features?.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => {
                // Show preview modal or navigate to sample
                if (index === 0) {
                  // MCQ Preview
                  document.getElementById('mcq-preview')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                {React.cloneElement(feature?.icon, { className: "w-8 h-8 text-white" })}
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{feature?.title}</h4>
              <p className="text-gray-600 text-sm mb-4">{feature?.description}</p>
              <div className="flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700">
                <span>{feature?.preview}</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      {/* Sample Questions Preview */}
      <div id="mcq-preview" className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
          Sample Interview Questions
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {sampleQuestions?.map((question, index) => (
            <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  question?.difficulty === 'Basic' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                }`}>
                  {question?.difficulty}
                </span>
                <span className="text-sm text-gray-500">{question?.category}</span>
              </div>
              
              <h4 className="font-semibold text-gray-900 mb-4">{question?.question}</h4>
              
              <div className="space-y-2">
                {question?.options?.map((option, optIndex) => (
                  <div 
                    key={optIndex} 
                    className={`p-3 rounded-lg border transition-colors ${
                      optIndex === question?.correct 
                        ? 'bg-green-50 border-green-200 text-green-800' :'bg-gray-50 border-gray-200 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium mr-3">
                        {String.fromCharCode(65 + optIndex)}
                      </span>
                      {option}
                      {optIndex === question?.correct && (
                        <CheckCircle className="w-5 h-5 text-green-600 ml-auto" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> {index === 0 
                    ? "Remember: Verification is about process compliance ('Are we building the product right?') while Validation is about meeting user needs ('Are we building the right product?')."
                    : "The Select class in Selenium WebDriver provides methods like selectByValue(), selectByVisibleText(), and selectByIndex() to handle dropdown interactions."
                  }
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-6">
          <p className="text-gray-600 mb-4">This is just a preview. Get access to 1000+ questions with detailed explanations!</p>
          <Button 
            onClick={() => navigate('/user-registration')}
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-3"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
      {/* Company-Specific Prep Preview */}
      <div>
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
          Company-Specific Preparation
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {companies?.map((company, index) => (
            <motion.div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer group"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">
                {company?.logo}
              </div>
              <h4 className="font-semibold text-gray-900 text-sm">{company?.name}</h4>
              <p className="text-xs text-gray-600">{company?.questions} questions</p>
            </motion.div>
          ))}
        </div>
      </div>
      {/* Benefits Section */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8">
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
          Why Choose Our Interview Preparation?
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits?.map((benefit, index) => (
            <div key={index} className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-gray-700">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-center text-white">
        <h3 className="text-3xl font-bold mb-4">Ready to Ace Your Testing Interview?</h3>
        <p className="text-xl text-blue-100 mb-6">
          Join thousands of successful testing professionals who started their journey here.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button 
            onClick={() => navigate('/user-registration')}
            className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 font-semibold"
          >
            Start Free Account
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            onClick={() => navigate('/user-login')}
            variant="outline"
            className="border-white text-white hover:bg-white/10 px-8 py-3"
          >
            Sign In
          </Button>
        </div>
        
        <div className="flex items-center justify-center mt-6 space-x-6 text-sm text-blue-100">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-300" />
            <span>100% Free to Start</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-300" />
            <span>No Credit Card Required</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewPrepPublicPreview;